import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import StatCard from "@/components/StatCard";
import api from "@/lib/api";
import { toast } from "sonner";
import { LayoutDashboard, FileSpreadsheet, Truck, Wallet, PackageCheck } from "lucide-react";

const NAV = [
  { to: "/vendor/dashboard", label: "Overview", icon: LayoutDashboard },
  { to: "/vendor/rfqs", label: "RFQs & Quotes", icon: FileSpreadsheet },
  { to: "/vendor/pos", label: "Purchase Orders", icon: Truck },
];

export default function VendorDashboard() {
  const [data, setData] = useState(null);
  const [rfqs, setRfqs] = useState([]);
  const [showQuote, setShowQuote] = useState(null);
  const [quote, setQuote] = useState({ unit_price: 0, delivery_days: 7, notes: "" });

  const load = async () => {
    const [d, r] = await Promise.all([api.get("/dashboard/vendor"), api.get("/rfqs")]);
    setData(d.data); setRfqs(r.data);
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    try {
      const item = showQuote.items[0];
      await api.post("/quotations", {
        rfq_id: showQuote.id,
        items: [{ name: item.name, qty: item.qty, unit_price: Number(quote.unit_price) }],
        notes: quote.notes, delivery_days: Number(quote.delivery_days),
      });
      toast.success("Quotation submitted");
      setShowQuote(null); setQuote({ unit_price: 0, delivery_days: 7, notes: "" });
      load();
    } catch { toast.error("Failed"); }
  };

  return (
    <DashboardLayout nav={NAV} title="Vendor Overview">
      {data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <StatCard label="Open RFQs" value={data.open_rfqs} icon={FileSpreadsheet} tone="blue" testid="v-rfqs" />
          <StatCard label="Quotations" value={data.quotations_submitted} icon={PackageCheck} tone="amber" testid="v-quotes" />
          <StatCard label="Purchase Orders" value={data.purchase_orders} icon={Truck} tone="green" testid="v-pos" />
          <StatCard label="Revenue" value={`₹${Number(data.revenue).toLocaleString('en-IN')}`} icon={Wallet} tone="slate" testid="v-rev" />
        </div>
      )}
      <div className="sl-card p-5">
        <h3 className="font-heading font-semibold mb-3">Incoming RFQs</h3>
        <div className="divide-y divide-slate-100">
          {rfqs.length === 0 && <div className="text-slate-500 text-sm py-6">No RFQs yet.</div>}
          {rfqs.map((r) => (
            <div key={r.id} className="py-3 flex items-center justify-between" data-testid={`vrfq-${r.id}`}>
              <div>
                <div className="font-semibold">{r.title}</div>
                <div className="text-xs text-slate-500">{r.items.length} line item(s) · {r.status}</div>
              </div>
              {r.status === "open" && <button onClick={() => setShowQuote(r)} data-testid={`vquote-${r.id}`} className="rounded-md bg-brand-blue text-white text-sm px-3 py-1.5 font-semibold hover:bg-brand-blueDark transition-colors duration-200">Submit Quote</button>}
            </div>
          ))}
        </div>
      </div>
      {showQuote && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowQuote(null)}>
          <div className="bg-white rounded-xl border border-slate-200 max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-heading font-bold text-lg">Quote for: {showQuote.title}</h3>
            <div className="text-sm text-slate-500 mt-1">Item: {showQuote.items[0].name} (Qty {showQuote.items[0].qty})</div>
            <div className="mt-4 space-y-3">
              <input type="number" placeholder="Unit price (₹)" value={quote.unit_price} onChange={(e) => setQuote({...quote, unit_price: e.target.value})} data-testid="q-price" className="w-full rounded-md border border-slate-300 px-3 py-2" />
              <input type="number" placeholder="Delivery days" value={quote.delivery_days} onChange={(e) => setQuote({...quote, delivery_days: e.target.value})} data-testid="q-days" className="w-full rounded-md border border-slate-300 px-3 py-2" />
              <textarea placeholder="Notes" value={quote.notes} onChange={(e) => setQuote({...quote, notes: e.target.value})} className="w-full rounded-md border border-slate-300 px-3 py-2" />
              <button onClick={submit} data-testid="q-submit" className="w-full rounded-md bg-brand-blue text-white font-semibold py-2 hover:bg-brand-blueDark transition-colors duration-200">Submit Quote</button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
